import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
	AddFundsFlow,
	OrderGroupResponse,
	RepeatOfferingInvestmentMetadata,
	RepeatOfferingInvestmentSelections,
	RepeatRiaInvestmentMetadata,
	RepeatRiaInvestmentSelections,
	RiaInvestTabAddFundsMetadata,
	SuggestedInvestmentAmountOption,
	TransactionType,
	ValidOfferingInvestmentRange,
	ValidRiaInvestmentRange
} from 'types/add-funds';
import {
	cacheOfferingSelections,
	cacheRiaSelections,
	getInvestTabAddFundsData,
	getRepeatOfferingInvestmentData,
	getRepeatOfferingInvestmentSelectionData,
	getRepeatRiaInvestmentData
} from '@api/add-funds';
import { CheckoutMixpanelEvent } from 'types/checkout';
import { CheckoutOrderGroupDetails } from 'types/checkout';
import { createOrderGroup } from '@src/api/checkout';
import { getRiaPlanById } from '@src/api/ria';
import { investmentEntity } from './investment-entity';
import { K1Acknowledgement } from 'types/form';
import { placeOrder } from '@api/checkout';
import { ReitType } from 'types/reit';
import { SelectOptionAmount } from 'types/layout';
import { ServiceLevelUpgradeName } from 'types/upgrade';
import store from '..';
import { upgrade } from './upgrade';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'addFunds',
	store
})
class AddFundsModule extends VuexModule {
	amount: string | null = null;
	cachedAddFundsSelections: RepeatRiaInvestmentSelections | null = null;
	cachedOfferingAddFundsSelections: RepeatOfferingInvestmentSelections | null = null;
	forceInvestUpgrade = false;
	investUpgrade: boolean | null = null;
	paymentMethodId = '';
	paymentMethodType: TransactionType | null = null;
	investTabAddFundsMetadata: RiaInvestTabAddFundsMetadata | null = null;
	suggestedInvestmentAmountOptions: Array<SuggestedInvestmentAmountOption> = [];
	suggestedAchOfferingInvestmentAmountOptions: Array<SelectOptionAmount> = [];
	suggestedWireOfferingInvestmentAmountOptions: Array<SelectOptionAmount> = [];
	reitInvestmentOrderGroupId = '';
	reitDocumentRevisionIds: Array<string> = [];
	validRiaInvestmentRange: ValidRiaInvestmentRange = {
		riaPlanId: '0',
		achMinInvestment: '0',
		achMaxInvestment: '0',
		debitMaxInvestment: '0',
		debitMinInvestment: '0'
	};
	validOfferingInvestmentRange: ValidOfferingInvestmentRange = {
		reitOfferingVintageId: '0',
		achMinInvestment: '0',
		achMaxInvestment: '0',
		debitMaxInvestment: '0',
		debitMinInvestment: '0',
		wireMinInvestment: '0',
		wireMaxInvestment: '0',
		ipoMinInvestment: '0',
		ipoMaxInvestment: '0'
	};
	orderGroupResponse: OrderGroupResponse | null = null;
	bankAccountLinked = false;
	investTabAddFunds = false;
	paymentCardLinked = false;
	orderComplete = false;
	riaDataLoaded = false;
	investTabAddFundsMetadataLoaded = false;
	offeringDataLoaded = false;
	amountAltText: string | null = null;
	offeringName: string | null = null;
	offeringType: ReitType | null = null;
	offeringId: string | null = null;
	offeringVintageId: string | null = null;
	addFundsFlow: AddFundsFlow | null = null;
	eligibleToInvestInOffering = false;
	acknowledgedK1Disclosure: K1Acknowledgement = { acknowledged: false };

	get checkoutMixpanelProperties(): CheckoutMixpanelEvent {
		let properties: CheckoutMixpanelEvent;
		if (this.addFundsFlow === 'EDIRECT') {
			properties = {
				'Order Type': 'Direct Investment',
				'Direct Investment': this.offeringName ?? ''
			};
		} else {
			properties = {
				'Order Type': 'RIA',
				'RIA Plan': this.investUpgrade ? upgrade.newRiaPlan?.name || '' : investmentEntity.currentRiaPlanName
			};
		}
		properties['Entity Type'] = investmentEntity.entityTypeLabel;
		properties['Transaction Type'] = this.paymentMethodType ?? 'ACH';

		if (this.reitInvestmentOrderGroupId && this.orderGroupResponse) {
			properties['Investment Amount'] = Number(this.amount);
			properties['Investments'] = this.orderGroupResponse.investments.map((investment) => {
				return investment.reitName;
			});
			if (this.bankAccountLinked) {
				properties['Payment Method Setup'] = 'New Account';
				properties['Payment Method Creation Method'] = 'Plaid';
			} else if (this.paymentCardLinked) {
				properties['Payment Method Setup'] = 'New Account';
				properties['Payment Method Creation Method'] = 'Stripe';
			} else {
				properties['Payment Method Setup'] = 'Existing Account';
			}
		}

		return properties as CheckoutMixpanelEvent;
	}

	@Action({ rawError: true })
	public async fetchAndStoreRepeatRiaInvestmentMetadata(): Promise<void> {
		const repeatRiaInvestmentMetadata = await getRepeatRiaInvestmentData();
		const serviceLevelUpgradeMappingData = repeatRiaInvestmentMetadata.serviceLevelUpgradeMapping;
		this.UPDATE_REPEAT_RIA_INVESTMENT_METADATA(repeatRiaInvestmentMetadata);
		upgrade.storeServiceLevelUpgradeData(serviceLevelUpgradeMappingData);
		this.UPDATE_RIA_DATA_LOADED(true);
	}

	@Action({ rawError: true })
	public async fetchAndStoreRepeatOfferingInvestmentMetadata(offeringVintageId: string): Promise<void> {
		const repeatOfferingInvestmentMetadata = await getRepeatOfferingInvestmentData(offeringVintageId);
		this.UPDATE_REPEAT_OFFERING_INVESTMENT_METADATA(repeatOfferingInvestmentMetadata);
		this.UPDATE_OFFERING_DATA_LOADED(true);
	}

	@Action({ rawError: true })
	public async fetchAndStoreInvestTabAddFundsMetadata(): Promise<void> {
		const [repeatRiaInvestmentMetadata, investTabAddFundsMetadata] = await Promise.all([
			getRepeatRiaInvestmentData(),
			getInvestTabAddFundsData()
		]);
		this.UPDATE_REPEAT_RIA_INVESTMENT_METADATA(repeatRiaInvestmentMetadata);
		this.UPDATE_INVEST_TAB_ADD_FUNDS_METADATA(investTabAddFundsMetadata);
		upgrade.storeServiceLevelUpgradeData(investTabAddFundsMetadata.serviceLevelUpgradeMapping);
		this.UPDATE_INVEST_TAB_ADD_FUNDS_METADATA_LOADED(true);
		this.UPDATE_RIA_DATA_LOADED(true);
	}

	@Action({ rawError: true })
	public clearPreviousOrder(): void {
		this.UPDATE_AMOUNT(null);
		this.UPDATE_PAYMENT_METHOD_ID('');
		this.UPDATE_PAYMENT_METHOD_TYPE(null);
		this.UPDATE_REIT_DOCUMENT_REVISION_IDS([]);
		this.UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID('');
		this.UPDATE_ORDER_COMPLETE(false);
		this.UPDATE_RIA_DATA_LOADED(false);
		this.UPDATE_INVEST_TAB_ADD_FUNDS_METADATA_LOADED(false);
		if (!this.forceInvestUpgrade) {
			this.UPDATE_INVEST_UPGRADE(null);
		}
	}

	@Action({ rawError: true })
	public resetForceUpgrade(): void {
		this.UPDATE_FORCE_INVEST_UPGRADE(false);
	}

	@Action({ rawError: true })
	public resetInvestTabAddFunds(): void {
		this.UPDATE_INVEST_TAB_ADD_FUNDS(false);
	}

	@Action({ rawError: true })
	public resetRiaDataLoaded(): void {
		this.UPDATE_RIA_DATA_LOADED(false);
	}

	@Action({ rawError: true })
	public resetInvestTabAddFundsMetadataLoaded(): void {
		this.UPDATE_INVEST_TAB_ADD_FUNDS_METADATA_LOADED(false);
	}

	@Action({ rawError: true })
	public async submitAgreements(reitDocumentRevisionIds: Array<string>): Promise<string> {
		this.UPDATE_REIT_DOCUMENT_REVISION_IDS(reitDocumentRevisionIds);
		if (this.addFundsFlow === 'EDIRECT') {
			return 'add-funds-offering-review';
		}
		return 'add-funds-ria-review';
	}

	@Action({ rawError: true })
	public async storeAndCacheRiaSelections(selections: RepeatRiaInvestmentSelections): Promise<void> {
		this.UPDATE_AMOUNT(selections.amount);
		this.UPDATE_PAYMENT_METHOD_ID(selections.bankAccountId ? selections.bankAccountId : selections.paymentMethodId);
		this.UPDATE_PAYMENT_METHOD_TYPE(selections.paymentMethodType);
		this.UPDATE_INVEST_UPGRADE(selections.serviceLevelUpgradeSelected);
		await cacheRiaSelections(selections);
	}

	@Action({ rawError: true })
	public async storeAndCacheOfferingSelections(selections: RepeatOfferingInvestmentSelections): Promise<void> {
		this.UPDATE_AMOUNT(selections.amount);
		this.UPDATE_PAYMENT_METHOD_ID(selections.paymentMethodId);
		this.UPDATE_PAYMENT_METHOD_TYPE(selections.paymentMethodType);
		await cacheOfferingSelections(selections);
	}

	@Action({ rawError: true })
	public async fetchAndUpdatePriorOfferingSelections(): Promise<void> {
		const priorSelections = await getRepeatOfferingInvestmentSelectionData();

		addFunds.UPDATE_OFFERING_VINTAGE_ID(priorSelections.offeringVintageId);
	}

	@Action({ rawError: true })
	public async createOfferingOrder(): Promise<string> {
		const order: CheckoutOrderGroupDetails = {
			amount: this.amount ?? '0',
			paymentMethodId: this.paymentMethodId,
			paymentMethodType: this.paymentMethodType,
			reitOfferingVintageId: this.offeringVintageId
		};
		const orderGroupResponse = await createOrderGroup(order);
		this.UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID(orderGroupResponse.orderGroupId ?? '');
		this.UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse);
		return 'add-funds-offering-agreements';
	}

	@Action({ rawError: true })
	public async createRiaOrder(orderDetails: {
		riaPlanId: string;
		serviceLevelUpgradeName: ServiceLevelUpgradeName | null;
	}): Promise<string> {
		const order: CheckoutOrderGroupDetails = {
			amount: this.amount ?? '0',
			paymentMethodId: this.paymentMethodId,
			paymentMethodType: this.paymentMethodType,
			riaPlanId: orderDetails.riaPlanId,
			serviceLevelUpgrade: orderDetails.serviceLevelUpgradeName
		};
		const orderGroupResponse = await createOrderGroup(order);
		this.UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID(orderGroupResponse.orderGroupId ?? '');
		this.UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse);
		return 'add-funds-ria-agreements';
	}

	@Action({ rawError: true })
	public async createRiaOrderFromInvestTab(): Promise<string> {
		if (!upgrade.newRiaPlan?.id || !upgrade.upgradeLevelName) {
			const currentPlan = await getRiaPlanById(investmentEntity.currentSelectedRiaPlanId);
			upgrade.UPDATE_NEW_RIA_PLAN(currentPlan);
			upgrade.updateUpgradeLevelData({
				name: investmentEntity.currentServiceLevel.name,
				label: investmentEntity.currentServiceLevel.label
			});
		}

		return 'upgrade-view-allocation';
	}

	@Action
	public parseDefaultAmountParam(defaultAmountQueryParam: string | null): void {
		if (defaultAmountQueryParam === 'minToUpgrade') {
			this.UPDATE_FORCE_INVEST_UPGRADE(true);
		}

		if (defaultAmountQueryParam !== null && !isNaN(Number(defaultAmountQueryParam))) {
			this.UPDATE_AMOUNT(defaultAmountQueryParam.toString());
		}
	}

	@Action({ rawError: true })
	public async submitReview(): Promise<string> {
		const orderGroupResponse = await placeOrder(
			this.reitInvestmentOrderGroupId,
			this.reitDocumentRevisionIds,
			null,
			null
		);

		this.UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse);

		this.UPDATE_ORDER_COMPLETE(true);

		if (this.investUpgrade) {
			await upgrade.postUpgradeActions();
		} else {
			await investmentEntity.refreshPositionsAndPerformance();
		}
		if (this.addFundsFlow === 'EDIRECT') {
			return 'add-funds-offering-success';
		}
		return 'add-funds-ria-success';
	}

	@Mutation
	public UPDATE_INVEST_UPGRADE(newVal: boolean | null): void {
		this.investUpgrade = newVal;
	}

	@Mutation
	public UPDATE_REIT_DOCUMENT_REVISION_IDS(reitDocumentRevisionIds: Array<string>): void {
		this.reitDocumentRevisionIds = reitDocumentRevisionIds;
	}

	@Mutation
	public UPDATE_REPEAT_RIA_INVESTMENT_METADATA(repeatRiaInvestmentMetadata: RepeatRiaInvestmentMetadata): void {
		this.validRiaInvestmentRange = repeatRiaInvestmentMetadata.validRiaInvestmentRange;
		this.suggestedInvestmentAmountOptions = repeatRiaInvestmentMetadata.suggestedInvestmentAmounts;
		this.cachedAddFundsSelections = repeatRiaInvestmentMetadata.repeatRiaInvestmentSelections ?? null;
		if (this.cachedAddFundsSelections !== null) {
			this.amount = this.cachedAddFundsSelections.amount;
			this.paymentMethodId = this.cachedAddFundsSelections.bankAccountId
				? this.cachedAddFundsSelections.bankAccountId
				: this.cachedAddFundsSelections.paymentMethodId;
			this.paymentMethodType = this.cachedAddFundsSelections.paymentMethodType;
			if (!this.forceInvestUpgrade) {
				// when making transition from upgrade landing -> add funds, we want to ignore whatever you previously cached for investUpgrade and force it to true
				this.investUpgrade = this.cachedAddFundsSelections.serviceLevelUpgradeSelected;
			}
		}
	}

	@Mutation
	public UPDATE_INVEST_TAB_ADD_FUNDS_METADATA(investTabAddFundsMetadata: RiaInvestTabAddFundsMetadata): void {
		this.validRiaInvestmentRange = investTabAddFundsMetadata.validRiaInvestmentRange;
		this.investTabAddFundsMetadata = investTabAddFundsMetadata;
	}

	@Mutation
	public UPDATE_REPEAT_OFFERING_INVESTMENT_METADATA(
		repeatOfferingInvestmentMetadata: RepeatOfferingInvestmentMetadata
	): void {
		this.eligibleToInvestInOffering = repeatOfferingInvestmentMetadata.eligibleToInvestInOffering;
		if (
			this.eligibleToInvestInOffering &&
			repeatOfferingInvestmentMetadata.validReitInvestmentRange &&
			repeatOfferingInvestmentMetadata.suggestedAchReitInvestmentAmounts &&
			repeatOfferingInvestmentMetadata.suggestedWireReitInvestmentAmounts
		) {
			this.validOfferingInvestmentRange = repeatOfferingInvestmentMetadata.validReitInvestmentRange;
			this.suggestedAchOfferingInvestmentAmountOptions =
				repeatOfferingInvestmentMetadata.suggestedAchReitInvestmentAmounts;
			this.suggestedWireOfferingInvestmentAmountOptions =
				repeatOfferingInvestmentMetadata.suggestedWireReitInvestmentAmounts;
		}
		this.cachedOfferingAddFundsSelections =
			repeatOfferingInvestmentMetadata.repeatOfferingInvestmentSelections ?? null;
		if (this.cachedOfferingAddFundsSelections !== null) {
			this.amount = this.cachedOfferingAddFundsSelections.amount;
			this.paymentMethodType = this.cachedOfferingAddFundsSelections.paymentMethodType;
			this.paymentMethodId = this.cachedOfferingAddFundsSelections.paymentMethodId;
		}
		this.offeringName = repeatOfferingInvestmentMetadata.offeringName;
		this.offeringType = repeatOfferingInvestmentMetadata.reitType;
		this.offeringId = repeatOfferingInvestmentMetadata.offeringId;
	}

	@Mutation
	public UPDATE_AMOUNT(amount: string | null): void {
		this.amount = amount;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD_ID(paymentMethodId: string): void {
		this.paymentMethodId = paymentMethodId;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD_TYPE(paymentMethodType: TransactionType | null): void {
		this.paymentMethodType = paymentMethodType;
	}

	@Mutation
	public UPDATE_REIT_INVESTMENT_ORDER_GROUP_ID(reitInvestmentOrderGroupId: string): void {
		this.reitInvestmentOrderGroupId = reitInvestmentOrderGroupId;
	}

	@Mutation
	public UPDATE_ORDER_COMPLETE(complete: boolean): void {
		this.orderComplete = complete;
	}

	@Mutation
	public UPDATE_FORCE_INVEST_UPGRADE(force: boolean): void {
		this.investUpgrade = force;
		this.forceInvestUpgrade = force;
	}

	@Mutation
	public UPDATE_ORDER_GROUP_RESPONSE(orderGroupResponse: OrderGroupResponse | null): void {
		this.orderGroupResponse = orderGroupResponse;
	}

	@Mutation
	public UPDATE_BANK_ACCOUNT_LINKED(bankAccountLinked: boolean): void {
		this.bankAccountLinked = bankAccountLinked;
	}

	@Mutation
	public UPDATE_PAYMENT_CARD_LINKED(paymentCardLinked: boolean): void {
		this.paymentCardLinked = paymentCardLinked;
	}

	@Mutation
	public UPDATE_OFFERING_DATA_LOADED(offeringDataLoaded: boolean): void {
		this.offeringDataLoaded = offeringDataLoaded;
	}

	@Mutation
	public UPDATE_RIA_DATA_LOADED(riaDataLoaded: boolean): void {
		this.riaDataLoaded = riaDataLoaded;
	}

	@Mutation
	public UPDATE_INVEST_TAB_ADD_FUNDS_METADATA_LOADED(investTabAddFundsMetadataLoaded: boolean): void {
		this.investTabAddFundsMetadataLoaded = investTabAddFundsMetadataLoaded;
	}

	@Mutation
	public UPDATE_AMOUNT_ALT_TEXT(amountAltText: string): void {
		this.amountAltText = amountAltText;
	}

	@Mutation
	public UPDATE_INVEST_TAB_ADD_FUNDS(investTabAddFunds: boolean): void {
		this.investTabAddFunds = investTabAddFunds;
	}

	@Mutation
	public UPDATE_OFFERING_VINTAGE_ID(offeringVintageId: string): void {
		this.offeringVintageId = offeringVintageId;
	}

	@Mutation
	public UPDATE_OFFERING_NAME(offeringName: string): void {
		this.offeringName = offeringName;
	}

	@Mutation
	public UPDATE_ADD_FUNDS_FLOW(addFundsFlow: AddFundsFlow): void {
		this.addFundsFlow = addFundsFlow;
	}

	@Mutation
	public UPDATE_ACKNOWLEDGED_K1_DISCLOSURE(acknowledgedK1Disclosure: K1Acknowledgement): void {
		this.acknowledgedK1Disclosure = acknowledgedK1Disclosure;
	}
}

export const addFunds = getModule(AddFundsModule);
